import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useStudentTeams } from 'context/StudentTeamsContext';
import {
  useUserProfiles,
  shortRoleLabel,
  roleColorClasses,
  roleLabelFor,
} from 'context/UserProfilesContext';
import { deriveNameFromEmail, initialsFor } from 'lib/currentUser';
import { humanizeSupabaseError } from '../../../lib/supabaseErrors';

// ---------------------------------------------------------------------------
// StudentTeamPanel
//
// "Team on this student" — shows every teammate who has been added to
// this student's IEP team, with their role pill and a small "remove"
// affordance. Below the roster:
//
//   * "Join this student's team" button if the current user isn't on
//     it yet. One click adds themselves.
//   * "Add someone else" dropdown of teammates from the workspace
//     roster who aren't already on this student's team.
//
// The section replaces the old free-text "Service Providers" list on
// the profile view: it's real data with real people, so downstream
// features (attribution filters, per-student report scoping, the
// printable one-pager's team box) work off of it directly.
// ---------------------------------------------------------------------------

const StudentTeamPanel = ({ studentId }) => {
  const {
    teamForStudent,
    addMember,
    removeMember,
    loading,
    isLive,
  } = useStudentTeams();
  const {
    profiles,
    me,
    getUserById,
    loading: profilesLoading,
  } = useUserProfiles();

  const [busyUserId, setBusyUserId] = useState(null);
  const [err, setErr] = useState('');
  const [addingUserId, setAddingUserId] = useState('');

  const team = teamForStudent(studentId);

  // Enrich team rows with the corresponding user_profile for rendering.
  // Anyone whose profile has been deleted (e.g. left the district) still
  // renders as "Former teammate" so historical membership isn't lost.
  const teamRows = useMemo(() => {
    return team
      .map((m) => ({ member: m, profile: getUserById(m.userId) }))
      .sort((a, b) => {
        // Current user pinned to top, then alphabetical by display name.
        if (me?.id && a.member.userId === me.id) return -1;
        if (me?.id && b.member.userId === me.id) return 1;
        const an =
          (a.profile?.displayName ||
            deriveNameFromEmail(a.profile?.email) ||
            'Zzz').toLowerCase();
        const bn =
          (b.profile?.displayName ||
            deriveNameFromEmail(b.profile?.email) ||
            'Zzz').toLowerCase();
        return an.localeCompare(bn);
      });
  }, [team, getUserById, me?.id]);

  // Candidates for "add someone else" — every workspace profile that
  // ISN'T already on this student's team.
  const addCandidates = useMemo(() => {
    const seen = new Set(team.map((m) => m.userId));
    return (profiles || [])
      .filter((p) => p?.id && !seen.has(p.id))
      .map((p) => ({
        value: p.id,
        label: `${
          p.displayName || deriveNameFromEmail(p.email) || p.email || 'Teammate'
        }${p.discipline ? ` (${p.discipline})` : p.role ? ` (${roleLabelFor(p.role)})` : ''}`,
      }));
  }, [profiles, team]);

  const currentUserOnTeam = Boolean(
    me?.id && team.some((m) => m.userId === me.id),
  );

  const handleJoin = async () => {
    if (!me?.id) return;
    setErr('');
    setBusyUserId(me.id);
    try {
      await addMember({ studentId, userId: me.id });
    } catch (error) {
      setErr(humanizeSupabaseError(error, 'Could not add you to this team.'));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleAddSomeone = async () => {
    if (!addingUserId) return;
    setErr('');
    setBusyUserId(addingUserId);
    try {
      await addMember({ studentId, userId: addingUserId });
      setAddingUserId('');
    } catch (error) {
      setErr(humanizeSupabaseError(error, 'Could not add teammate to this student.'));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRemove = async (userId, name) => {
    const label = name || 'this teammate';
    if (
      !window.confirm(
        `Remove ${label} from this student's team?\n\nAny data they've already logged stays on the record.`,
      )
    ) {
      return;
    }
    setErr('');
    setBusyUserId(userId);
    try {
      await removeMember({ studentId, userId });
    } catch (error) {
      setErr(humanizeSupabaseError(error, 'Could not remove teammate.'));
    } finally {
      setBusyUserId(null);
    }
  };

  const showLoader = loading || profilesLoading;

  return (
    <section className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <header className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-card-foreground flex items-center gap-2">
          <Icon name="Users2" size={18} />
          Team on this student
          {!showLoader && (
            <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {team.length}
            </span>
          )}
        </h3>
      </header>

      <p className="text-sm text-muted-foreground mb-4">
        Everyone here can log data, update goals, and see this student's
        records live. Add the SLP, OT, PT, behavior specialist, and paras
        who work with this student so every data point they log gets
        credited to them.
      </p>

      {err && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive"
        >
          {err}
        </div>
      )}

      {showLoader ? (
        <div className="py-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Loader2" size={16} className="animate-spin" />
          Loading team…
        </div>
      ) : team.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-center text-sm text-muted-foreground mb-4">
          No one has joined this student's team yet. Add yourself first,
          then invite the other people who work with them.
        </div>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-lg mb-4">
          {teamRows.map(({ member, profile }) => {
            const name =
              profile?.displayName ||
              deriveNameFromEmail(profile?.email) ||
              (profile?.email) ||
              'Former teammate';
            const isMe = me?.id === member.userId;
            const short = shortRoleLabel(profile);
            const roleColor = roleColorClasses(profile);
            const roleFull = profile?.discipline || roleLabelFor(profile?.role);
            const isBusy = busyUserId === member.userId;
            return (
              <li
                key={member.id}
                className={`flex items-center gap-3 p-3 ${isMe ? 'bg-primary/5' : ''}`}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${roleColor}`}
                  aria-hidden="true"
                >
                  {initialsFor(name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-card-foreground truncate">
                      {name}
                      {isMe && (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          (you)
                        </span>
                      )}
                    </p>
                    {short && (
                      <span
                        className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${roleColor}`}
                      >
                        {short}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {roleFull || 'Role not set'}
                    {profile?.email ? (
                      <>
                        <span aria-hidden="true"> · </span>
                        {profile.email}
                      </>
                    ) : null}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(member.userId, name)}
                  disabled={isBusy}
                  iconName={isBusy ? 'Loader2' : 'X'}
                  iconPosition="left"
                  iconSize={14}
                  className="text-muted-foreground hover:text-destructive"
                  title={isMe ? 'Leave this team' : `Remove ${name}`}
                >
                  {isMe ? 'Leave' : 'Remove'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        {!currentUserOnTeam && me?.id && (
          <Button
            variant="default"
            size="sm"
            onClick={handleJoin}
            disabled={busyUserId === me.id}
            iconName={busyUserId === me.id ? 'Loader2' : 'UserPlus'}
            iconPosition="left"
            iconSize={14}
          >
            Add me to this team
          </Button>
        )}
        {addCandidates.length > 0 && (
          <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1 min-w-0">
              <Select
                label={
                  currentUserOnTeam ? 'Add someone else' : 'Or add someone else'
                }
                placeholder="Choose a teammate…"
                searchable
                options={addCandidates}
                value={addingUserId}
                onChange={setAddingUserId}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddSomeone}
              disabled={!addingUserId || busyUserId === addingUserId}
              iconName={busyUserId === addingUserId ? 'Loader2' : 'Plus'}
              iconPosition="left"
              iconSize={14}
            >
              Add
            </Button>
          </div>
        )}
        {addCandidates.length === 0 && !showLoader && team.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Everyone in your workspace is already on this student's team.
          </p>
        )}
      </div>

      {!isLive && (
        <p className="mt-4 text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2">
          Demo mode: changes here are stored locally on this browser. Sign
          in with real Supabase credentials to share team assignments
          with the rest of your workspace.
        </p>
      )}
    </section>
  );
};

export default StudentTeamPanel;
